import { useEffect, useCallback, useMemo } from 'react';
import { TextContentLightboxConfig } from '../types/TextContentLightbox';

interface UseTextContentNavigationProps<T> {
  currentItem: T | null;
  allItems: T[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadMore?: () => void;
  onNavigate?: (item: T) => void;
  getItemId: (item: T) => string;
  config: TextContentLightboxConfig;
}

export const useTextContentNavigation = <T>({
  currentItem,
  allItems,
  hasNextPage = false,
  isFetchingNextPage = false,
  loadMore,
  onNavigate,
  getItemId,
  config
}: UseTextContentNavigationProps<T>) => {
  
  // Memoized calculations for better performance
  const navigationState = useMemo(() => {
    // Ensure we always have a valid array
    const validItems = Array.isArray(allItems) ? allItems.filter(Boolean) : [];
    
    // Safe index calculation
    let currentIndex = -1;
    if (currentItem && validItems.length > 0) {
      try {
        currentIndex = validItems.findIndex(item => {
          if (!item) return false;
          try {
            return getItemId(item) === getItemId(currentItem);
          } catch {
            return false;
          }
        });
      } catch {
        currentIndex = -1;
      }
    }
    
    return {
      validItems,
      currentIndex,
      hasPrevious: currentIndex > 0,
      hasNext: currentIndex >= 0 && currentIndex < validItems.length - 1,
      totalItems: validItems.length
    };
  }, [currentItem, allItems, getItemId]);

  const { validItems, currentIndex, hasPrevious, hasNext, totalItems } = navigationState;

  // Optimized navigation handlers with useCallback
  const handlePrevious = useCallback(() => {
    if (hasPrevious && onNavigate) {
      const prevIndex = Math.max(0, currentIndex - 1);
      if (validItems[prevIndex]) {
        onNavigate(validItems[prevIndex]);
      }
    }
  }, [hasPrevious, onNavigate, currentIndex, validItems]);

  const handleNext = useCallback(() => {
    if (hasNext && onNavigate) {
      const nextIndex = Math.min(validItems.length - 1, currentIndex + 1);
      if (validItems[nextIndex]) {
        onNavigate(validItems[nextIndex]);
      }
    } else if (!hasNext && hasNextPage && !isFetchingNextPage && loadMore) {
      // At end but more available - trigger load
      loadMore();
    }
  }, [hasNext, onNavigate, currentIndex, validItems, hasNextPage, isFetchingNextPage, loadMore]);

  // Optimized keyboard navigation
  useEffect(() => {
    if (!config.navigation?.keyboard) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere with typing in input fields
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNext();
          break;
        case 'Escape':
          // Let the dialog handle escape
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [config.navigation?.keyboard, handlePrevious, handleNext]);

  // Optimized preloading logic with throttling
  useEffect(() => {
    if (!config.preloading?.enabled || !currentItem || !loadMore) return;

    const { strategy, preloadDistance } = config.preloading;
    
    // Smart preloading with better logic
    const shouldPreload = () => {
      switch (strategy) {
        case 'aggressive':
          // Load when approaching last quarter
          return currentIndex >= totalItems - Math.ceil(totalItems * 0.25);
        case 'conservative':
          // Load only when at last item
          return currentIndex === totalItems - 1;
        case 'smart':
        default:
          // Load when within preloadDistance of end
          return currentIndex >= totalItems - preloadDistance;
      }
    };

    if (shouldPreload() && hasNextPage && !isFetchingNextPage) {
      // Throttle preloading to prevent excessive requests
      const timeoutId = setTimeout(() => {
        loadMore();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [config.preloading, currentItem, currentIndex, totalItems, hasNextPage, isFetchingNextPage, loadMore]);

  // Determine if we can navigate to next (for UI state)
  const canNavigateNext = hasNext || (hasNextPage && !isFetchingNextPage);

  return {
    hasPrevious,
    hasNext,
    canNavigateNext,
    handlePrevious,
    handleNext,
    currentIndex,
    totalItems
  };
}; 